import { __awaiter, __decorate } from "tslib";
import { Auth, Connect, dataServerResultFilter, extractFieldDefinitions, FieldTypeEnum, MessageType, normaliseCriteria, toFieldMetadata, } from '@genesislcap/foundation-comms';
import { LifecycleMixin } from '@genesislcap/foundation-utils';
import { attr, customElement, DOM } from '@microsoft/fast-element';
import { take } from 'rxjs/operators';
import { dateTimeValueFormatter, dateValueFormatter } from '../grid-pro.definitions';
import { GridProRendererTypes } from '../grid-pro.types';
import { logger } from '../utils';
import { GridProBaseDatasource } from './base.datasource';
import { getColumnType, getFilterByFieldType, getFilterParamsByFieldType, } from './server-side.grid-definitions';
/**
 * The IServerSideDatasource implementation, used for SSRM (Server-Side Row Model) in the grid.
 * @alpha
 */
export class StreamDatasource {
    constructor(options) {
        /**
         * The number of rows in the grid. THIS DO NOT CONSIDER CRITERIA_MATCH scenarios.
         * @internal
         */
        this.server_ROWS_COUNT = 0;
        /**
         * The number of rows in the grid. THIS IS A LOCAL COUNT CONSIDERING CONSIDER CRITERIA_MATCH scenarios.
         * @internal
         */
        this.client_ROWS_COUNT = 0;
        this.moreRows = false;
        this.calculatedRowsCount = 0;
        this.lastSequenceId = 0;
        this.rowData = new Map();
        this.dataserverStream = options.stream;
        this.resourceName = options.resourceName;
        this.resourceParams = options.resourceParams;
        this.originalCriteriaMatch = options.resourceParams.CRITERIA_MATCH;
        this.resourceIndexes = options.resourceIndexes;
        this.resourceColDefs = options.resourceColDefs;
        this.maxRows = options.maxRows;
        this.maxView = options.maxView;
        this.rowId = options.rowId;
        this.pagination = options.pagination;
    }
    getRows(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const filterModelBeingApplied = params.request.filterModel;
            const filtersAreBeingApplied = Object.keys(filterModelBeingApplied).length > 0;
            const filtersAreGettingCleaned = Object.keys(filterModelBeingApplied).length === 0;
            const filterModelBeingAppliedDiffersFromCurrent = JSON.stringify(filterModelBeingApplied) !== JSON.stringify(this.currentFilterModel);
            if (filterModelBeingAppliedDiffersFromCurrent || filtersAreBeingApplied) {
                if (filtersAreGettingCleaned && this.currentFilterModel) {
                    this.currentFilterModel = null;
                    this.resourceParams.CRITERIA_MATCH = this.originalCriteriaMatch;
                    this.restartSubscription();
                }
                else if (filterModelBeingAppliedDiffersFromCurrent && filtersAreBeingApplied) {
                    this.currentFilterModel = filterModelBeingApplied;
                    this.resourceParams.CRITERIA_MATCH = this.buildCriteriaMatchFromFilters();
                    this.restartSubscription();
                }
            }
            const toBeAppliedSortModel = params.request.sortModel;
            if (toBeAppliedSortModel.length !== ((_a = this.currentSortModel) === null || _a === void 0 ? void 0 : _a.length) ||
                toBeAppliedSortModel.length > 0) {
                if (toBeAppliedSortModel.length === 0 && this.currentSortModel) {
                    this.currentSortModel = null;
                    this.resourceParams.ORDER_BY = null;
                    this.resourceParams.REVERSE = null;
                    this.restartSubscription();
                }
                else if (toBeAppliedSortModel.length > 0) {
                    this.currentSortModel = toBeAppliedSortModel;
                    const coldIdBeingSorted = this.currentSortModel[0].colId; // Not allowing multiple sorts by user
                    const sortTypeBeingApplied = this.currentSortModel[0].sort;
                    const orderByAndToBeSortedColIds = this.getOrderByAndToBeSortedColIds(this.resourceIndexes, coldIdBeingSorted);
                    orderByAndToBeSortedColIds.toBeSorted.forEach((colId) => {
                        this.currentSortModel.push({ sort: sortTypeBeingApplied, colId });
                    });
                    this.resourceParams.ORDER_BY = orderByAndToBeSortedColIds.orderBy;
                    this.resourceParams.REVERSE = sortTypeBeingApplied === 'desc' ? true : false;
                    this.restartSubscription();
                }
            }
            if (this.moreRows && params.request.startRow >= Number(this.maxRows)) {
                this.connect.getMoreRows(this.streamSourceRef);
            }
            // TODO: take(1) is temporary, won't have to do this once SSRM Transactions are implemented
            this.dataserverStream.pipe(take(1)).subscribe((result) => {
                var _a, _b;
                const messageType = result.MESSAGE_TYPE;
                if (messageType &&
                    (messageType === MessageType.LOGOFF_ACK || messageType === MessageType.MSG_NACK)) {
                    params.fail();
                    return;
                }
                this.moreRows = result.MORE_ROWS;
                if (result.ROW) {
                    const nextMessage = dataServerResultFilter(result);
                    this.handleInitialStreamLoad(nextMessage);
                }
                else {
                    this.rowData = new Map();
                    const successRowData = { rowData: Array.from(this.rowData.values()) };
                    successRowData.rowCount = 0;
                    params.success(successRowData);
                    return;
                }
                // TODO: SSRM Transactions - https://www.ag-grid.com/javascript-data-grid/server-side-model-updating-transactions/
                // if (messageType === MessageType.QUERY_UPDATE) {
                //   if (result.ROW) {
                //     const nextMessage = dataServerResultFilter(result);
                //     this.agTransaction = { add: [], remove: [], update: [] };
                //     this.handleStreamInserts(nextMessage.inserts);
                //     this.handleStreamDeletes(nextMessage.deletes);
                //     this.handleStreamUpdates(nextMessage.updates);
                //     params.api.applyServerSideTransactionAsync(this.agTransaction);
                //   }
                // }
                this.lastSequenceId = result.SEQUENCE_ID;
                if (this.lastSequenceId === 1) {
                    this.streamSourceRef = result.SOURCE_REF;
                    this.server_ROWS_COUNT = (_a = result.ROWS_COUNT) !== null && _a !== void 0 ? _a : this.rowData.size;
                }
                // TODO: TEMPORARY - Due ROWS_COUNT not respecting CRITERIA_MATCH scenarios.
                if (!this.moreRows && this.server_ROWS_COUNT >= this.client_ROWS_COUNT) {
                    this.calculatedRowsCount = this.client_ROWS_COUNT;
                }
                else {
                    this.calculatedRowsCount += (_b = this.rowData.size) !== null && _b !== void 0 ? _b : 0;
                }
                const successRowData = { rowData: Array.from(this.rowData.values()) };
                if (this.pagination) {
                    successRowData.rowCount = Math.min(this.server_ROWS_COUNT, this.maxView);
                }
                if (params.request.endRow > this.maxView ||
                    params.request.endRow > this.calculatedRowsCount) {
                    successRowData.rowCount = this.calculatedRowsCount;
                }
                else if (params.request.endRow === this.server_ROWS_COUNT) {
                    successRowData.rowCount = this.server_ROWS_COUNT;
                }
                params.success(successRowData);
            });
        });
    }
    // TODO: SSRM Transactions - https://www.ag-grid.com/javascript-data-grid/server-side-model-updating-transactions/
    // private handleStreamInserts(insertedRows: any[]) {
    //   insertedRows?.forEach((insertData) => {
    //     if (this.rowData.has(insertData[this.rowId])) {
    //       const rowToBeUpdated = this.rowData.get(insertData[this.rowId]);
    //       const updatedRow = { ...rowToBeUpdated, ...insertData };
    //       this.agTransaction.update.push(updatedRow);
    //     } else {
    //       this.agTransaction.add.push(insertData);
    //       this.agTransaction.addIndex = 0;
    //     }
    //     this.rowData.set(insertData[this.rowId], insertData);
    //   });
    // }
    // private handleStreamDeletes(deletedRows: any[]) {
    //   deletedRows?.forEach((deleteData) => {
    //     this.agTransaction.remove.push({ [this.rowId]: deleteData[this.rowId] });
    //     this.rowData.delete(deleteData[this.rowId]);
    //   });
    // }
    // private handleStreamUpdates(updatedRows: any[]) {
    //   updatedRows?.forEach((updateData) => {
    //     const rowToBeUpdated = this.rowData.get(updateData[this.rowId]);
    //     const updatedRow = { ...rowToBeUpdated, ...updateData };
    //     this.agTransaction.update.push(updatedRow);
    //     this.rowData.set(updateData[this.rowId], updatedRow);
    //   });
    // }
    dataLogoff() {
        const shouldPerformDataLogoff = this.streamSourceRef;
        if (shouldPerformDataLogoff && this.auth.isLoggedIn) {
            this.connect.dataLogoff(this.streamSourceRef);
        }
        this.streamSourceRef = undefined;
    }
    restartSubscription() {
        this.dataLogoff();
        this.rowData = new Map();
        this.client_ROWS_COUNT = 0;
        const streamOnMessage = (message) => message;
        const onError = (error) => logger.error(error);
        this.dataserverStream = this.connect.stream(this.resourceName, streamOnMessage, onError, this.resourceParams);
    }
    getOrderByAndToBeSortedColIds(map, searchValue) {
        for (const [key, value] of map.entries()) {
            const searchIndex = value.findIndex((x) => x === searchValue);
            if (searchIndex !== -1) {
                const copy = [...value];
                copy.splice(searchIndex, 1);
                return { orderBy: key, toBeSorted: copy };
            }
        }
    }
    handleInitialStreamLoad(result) {
        var _a;
        if (!result)
            return;
        const rows = new Map();
        (_a = result.inserts) === null || _a === void 0 ? void 0 : _a.forEach((insertData) => {
            rows.set(insertData.ROW_REF, insertData);
        });
        // TODO: TEMPORARY - Due ROWS_COUNT not being in sync with CRITERIA_MATCH scenarios.
        this.client_ROWS_COUNT += rows.size;
        this.rowData = rows;
    }
    buildCriteriaMatchFromFilters() {
        this.resourceParams.CRITERIA_MATCH = this.originalCriteriaMatch;
        if (this.resourceParams.CRITERIA_MATCH &&
            typeof this.resourceParams.CRITERIA_MATCH === 'string') {
            this.resourceParams.CRITERIA_MATCH = this.resourceParams.CRITERIA_MATCH.split(';');
        }
        if (!this.resourceParams.CRITERIA_MATCH) {
            this.resourceParams.CRITERIA_MATCH = [];
        }
        const criteria = this.resourceParams.CRITERIA_MATCH.concat(this.criteriaFromFilters());
        return criteria.join(' && ');
    }
    // TODO: update this following https://learn.genesis.global/docs/server/data-server/advanced/#common-expressions
    criteriaFromFilters() {
        const filters = [];
        this.getFiltersByType('text').forEach((k) => {
            // TODO: no official "boolean" filter type, it's a "text"
            if (!this.currentFilterModel[k].filter &&
                (this.currentFilterModel[k].type === 'false' || this.currentFilterModel[k].type === 'true')) {
                filters.push(`${k} == ${this.currentFilterModel[k].type}`);
            }
            else {
                switch (this.currentFilterModel[k].type) {
                    case 'equals':
                        filters.push(`${k} == '${this.currentFilterModel[k].filter}'`);
                        break;
                    case 'contains':
                        filters.push(`Expr.containsIgnoreCase(${k}, '${this.currentFilterModel[k].filter}')`);
                        break;
                }
            }
        });
        this.getFiltersByType('number').forEach((k) => {
            const value = this.currentFilterModel[k].filter;
            switch (this.currentFilterModel[k].type) {
                case 'equals':
                    !isNaN(value) && filters.push(`${k} == ${value}`);
                    break;
                case 'lessThan':
                    !isNaN(value) && filters.push(`${k} < ${value}`);
                    break;
                case 'greaterThan':
                    !isNaN(value) && filters.push(`${k} > ${value}`);
                    break;
            }
        });
        this.getFiltersByType('date').forEach((k) => {
            const dateFrom = this.currentFilterModel[k].dateFrom
                .replace(/-/g, '')
                .replace('T', '-')
                .split(' ')[0];
            switch (this.currentFilterModel[k].type) {
                case 'equals':
                    filters.push(`Expr.dateIsEqual(${k}, '${dateFrom}')`);
                    break;
                case 'lessThan':
                    filters.push(`Expr.dateIsBefore(${k}, '${dateFrom}')`);
                    break;
                case 'greaterThan':
                    filters.push(`Expr.dateIsAfter(${k}, '${dateFrom}')`);
                    break;
                default:
                    break;
            }
        });
        return filters;
    }
    getFiltersByType(filterType) {
        return Object.keys(this.currentFilterModel)
            .filter((k) => this.resourceColDefs.findIndex((o) => o.NAME === k) !== -1)
            .filter((k) => this.currentFilterModel[k].filterType === filterType);
    }
}
__decorate([
    Auth
], StreamDatasource.prototype, "auth", void 0);
__decorate([
    Connect
], StreamDatasource.prototype, "connect", void 0);
const criteriaDelimiter = ';';
/**
 * A Genesis Datasource element, for server-side | SSRM-compatible data fetching and used exclusively by the GridPro element.
 * @remarks Only supports Server-Side Row Model. Requires `@ag-grid-enterprise/server-side-row-model` setup and valid AG Grid Enterprise license.
 * @alpha
 */
let GridProServerSideDatasource = class GridProServerSideDatasource extends LifecycleMixin(GridProBaseDatasource) {
    constructor() {
        super(...arguments);
        this.pagination = false;
        this.rowId = 'ROW_REF';
    }
    criteriaChanged(oldCriteria, newCriteria) {
        const startingCriteria = !oldCriteria && newCriteria;
        const criteriaNotDuplicate = oldCriteria !== normaliseCriteria(newCriteria, criteriaDelimiter);
        // TODO: FUI-1323 - account for linked grid scenarios too.
        // TODO: check grid-pro-genesis-datasource criteriaChanged
        if (!startingCriteria && criteriaNotDuplicate) {
            this.restart();
        }
    }
    resourceNameChanged(oldValue, newValue) {
        if (!oldValue || oldValue === newValue)
            return;
        this.restart();
    }
    connectedCallback() {
        super.connectedCallback();
        const shouldRunConnect = this.shouldRunConnect;
        DOM.queueUpdate(() => {
            if (!shouldRunConnect)
                return;
            this.init();
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        const shouldRunDisconnect = this.shouldRunDisconnect;
        DOM.queueUpdate(() => {
            if (!shouldRunDisconnect)
                return;
            this.reset();
        });
    }
    deepClone() {
        const copy = super.deepClone();
        copy.deferredGridOptions = structuredClone(this.deferredGridOptions);
        copy.deferredColumnStates = structuredClone(this.deferredColumnStates);
        return copy;
    }
    init() {
        if (this.agGrid && this.resourceName && this.connect.isConnected) {
            this.agGrid.gridOptions = Object.assign({ getRowId: (params) => {
                    if (!params.data) {
                        return null;
                    }
                    return params.data[this.rowId];
                }, cacheBlockSize: this.maxRows, defaultColDef: {
                    filter: true,
                    resizable: true,
                    sortable: true,
                }, pagination: this.pagination, paginationPageSize: this.maxRows, rowBuffer: 0, rowModelType: 'serverSide', suppressServerSideInfiniteScroll: false, suppressMultiSort: true, onFilterChanged: (params) => { }, onSortChanged: (params) => { } }, this.deferredGridOptions);
            this.agGrid.addEventListener('onGridReady', () => __awaiter(this, void 0, void 0, function* () {
                const meta = yield this.connect
                    .getMetadata(this.resourceName)
                    .catch((e) => logger.error(e));
                if (!meta)
                    return false;
                this.indexes = this.getResourceIndexes(meta.INDEXES);
                this.originalFieldDef = extractFieldDefinitions(meta);
                const fieldMetadata = toFieldMetadata(this.originalFieldDef);
                const agColumnDefs = this.getAgColumnDefs(fieldMetadata);
                this.agGrid.gridApi.setColumnDefs(agColumnDefs);
                const dataserverStream = yield this.createDataserverStream();
                this.dataserverStream = new StreamDatasource({
                    stream: dataserverStream,
                    resourceName: this.resourceName,
                    resourceParams: this.params,
                    resourceIndexes: this.indexes,
                    resourceColDefs: this.originalFieldDef,
                    maxRows: this.maxRows,
                    maxView: this.maxView,
                    rowId: this.rowId,
                    pagination: this.pagination,
                });
                this.agGrid.gridApi.setServerSideDatasource(this.dataserverStream);
            }), { once: true });
            return;
        }
        logger.warn(`Application not connected, falling back to local columnDefs/rowData`);
    }
    reset() {
        var _a;
        this.clearRowData();
        this.dataserverStream = undefined;
        this.originalFieldDef = undefined;
        (_a = this.agGrid.gridApi) === null || _a === void 0 ? void 0 : _a.setServerSideDatasource(this.dataserverStream);
    }
    restart() {
        this.reset();
        this.init();
    }
    get agGrid() {
        return this.parentElement;
    }
    clearRowData() {
        var _a, _b, _c, _d;
        (_b = (_a = this.agGrid) === null || _a === void 0 ? void 0 : _a.gridApi) === null || _b === void 0 ? void 0 : _b.setColumnDefs([]);
        const agTransaction = { remove: [] };
        (_c = this.agGrid.gridApi) === null || _c === void 0 ? void 0 : _c.forEachNode((node) => {
            agTransaction.remove.push(node.data);
        });
        (_d = this.agGrid.gridApi) === null || _d === void 0 ? void 0 : _d.applyServerSideTransaction(agTransaction);
    }
    getResourceIndexes(avaialbleIndexes) {
        const resourceIndexesMap = new Map();
        avaialbleIndexes === null || avaialbleIndexes === void 0 ? void 0 : avaialbleIndexes.forEach((index) => {
            resourceIndexesMap.set(index.NAME, index.FIELDS.split(' '));
        });
        return resourceIndexesMap;
    }
    get params() {
        const params = {
            CRITERIA_MATCH: this.criteria,
            FIELDS: this.fields,
            MAX_ROWS: this.maxRows,
            MAX_VIEW: this.maxView,
            MOVING_VIEW: this.movingView,
            REVERSE: this.reverse,
        };
        const keys = Array.from(this.indexes.keys());
        if (this.orderBy) {
            if (keys.findIndex((key) => key === this.orderBy) > -1) {
                params.ORDER_BY = this.orderBy;
            }
            else {
                const validIndexes = keys.map((key) => key);
                logger.warn('Specified [orderBy] index is not valid and will not be used. See https://learn.genesis.global/docs/database/data-types/index-entities/ - Available indexes:', validIndexes);
            }
        }
        logger.debug('Getting configured params:', params);
        return params;
    }
    createDataserverStream(existingParams = null) {
        return __awaiter(this, void 0, void 0, function* () {
            const streamOnMessage = (message) => logger.debug(message);
            const onError = (error) => logger.error(error);
            return this.connect.stream(this.resourceName, streamOnMessage, onError, existingParams !== null && existingParams !== void 0 ? existingParams : this.params);
        });
    }
    getAgColumnDefs(fieldsMetadata) {
        const colDefsFromGenesisData = fieldsMetadata === null || fieldsMetadata === void 0 ? void 0 : fieldsMetadata.flatMap((field) => {
            var _a;
            const colDefBase = {};
            colDefBase.filterParams = getFilterParamsByFieldType(field.type);
            switch (field.type) {
                case FieldTypeEnum.NUMBER:
                    colDefBase.cellEditor = GridProRendererTypes.numberEditor;
                    break;
                case FieldTypeEnum.BOOLEAN:
                    if (this.agGrid.autoCellRendererByType) {
                        colDefBase.cellRenderer = GridProRendererTypes.boolean;
                        colDefBase.cellEditor = GridProRendererTypes.boolean;
                    }
                    break;
                case FieldTypeEnum.DATE:
                    colDefBase.valueFormatter = dateValueFormatter;
                    break;
                case FieldTypeEnum.DATETIME:
                    colDefBase.valueFormatter = dateTimeValueFormatter;
                    break;
                case FieldTypeEnum.ENUM:
                    colDefBase.cellEditor = GridProRendererTypes.selectEditor;
                    colDefBase.cellEditorParams = {
                        values: field.enumOptions.map((option) => ({ value: option, label: option })),
                        valueField: 'value',
                        labelField: 'label',
                    };
                    break;
                default:
                    break;
            }
            const templateColDef = (_a = this.agGrid.gridApi) === null || _a === void 0 ? void 0 : _a.getColumnDef(field.name);
            if (this.fields && !this.fields.includes(field.name)) {
                return [];
            }
            return Object.assign(Object.assign({ field: field.name, type: getColumnType(field.type), filter: getFilterByFieldType(field.type), sortable: [].concat(...this.indexes.values()).includes(field.name) }, colDefBase), templateColDef);
        });
        const colDefsMergedWithTemplateDefs = this.agGrid.mergeAllColumnDefsAndStates(colDefsFromGenesisData, true);
        return colDefsMergedWithTemplateDefs;
    }
};
__decorate([
    attr({ mode: 'boolean' })
], GridProServerSideDatasource.prototype, "pagination", void 0);
__decorate([
    attr({ attribute: 'row-id' })
], GridProServerSideDatasource.prototype, "rowId", void 0);
GridProServerSideDatasource = __decorate([
    customElement({
        name: 'grid-pro-server-side-datasource',
    })
], GridProServerSideDatasource);
export { GridProServerSideDatasource };
